/**
 * Review Center
 * One-stop review hub: today's queue, custom (exam-prep) practice, weak-point focus.
 */
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TodayTab from './tabs/TodayTab';
import CustomTab from './tabs/CustomTab';
import WeakPointsTab from './tabs/WeakPointsTab';
import TrialCountdownWidget from '../../components/onboarding/TrialCountdownWidget';
import './ReviewCenterPage.css';

type TabId = 'today' | 'custom' | 'weak';

const TAB_ORDER: TabId[] = ['today', 'custom', 'weak'];

const ReviewCenterPage: React.FC = () => {
  const { t } = useTranslation('review');
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo<TabId>(() => {
    const raw = searchParams.get('tab');
    return TAB_ORDER.includes(raw as TabId) ? (raw as TabId) : 'today';
  }, [searchParams]);

  const setTab = (tab: TabId) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'today') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <main className="rc-page">
      <header className="rc-header">
        <h1 className="rc-title">{t('title')}</h1>
        <p className="rc-subtitle">{t('subtitle')}</p>
      </header>

      {/* Trial-only widget — renders null for non-trialing users so the
          card never appears outside the trial window. Lives above the
          tabs so trialing learners see the countdown the moment they
          land on the daily-review hub. */}
      <TrialCountdownWidget />

      <div className="rc-tabs" role="tablist">
        {TAB_ORDER.map(tab => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            className={`rc-tab ${activeTab === tab ? 'rc-tab--active' : ''}`}
            onClick={() => setTab(tab)}
          >
            {t(`tabs.${tab}`)}
          </button>
        ))}
      </div>

      <section className="rc-tab-panel" role="tabpanel">
        {activeTab === 'today' && <TodayTab />}
        {activeTab === 'custom' && <CustomTab />}
        {activeTab === 'weak' && <WeakPointsTab />}
      </section>
    </main>
  );
};

export default ReviewCenterPage;
